import {Context as TContext, createContext, FC, ReactNode, useContext, useEffect, useState} from "react";
import {ILogin, MQTTStateType} from "../types";
import mqtt, {IClientOptions, MqttClient, QoS} from "mqtt";
import useAuthStore from "contexts/AuthStore";

type TProps = {
  children: ReactNode;
};

const Context: TContext<MQTTStateType> = createContext<MQTTStateType>({
  mqttConnect: () => {},
  mqttSub: () => {},
  mqttUnSub: async () => {},
  mqttPublish: () => {},
  mqttDisconnect: async () => {},
  payload: {
    topic: "",
    message: ""
  },
  setPayload: () => {},
  isSub: false,
  saveLogin: () => {},
  userMQTT: "",
  connectStatus: ""
});

const useMQTT = () => useContext<MQTTStateType>(Context);

const useAppState = (): MQTTStateType => {
  const [payload, setPayload] = useState({topic: "", message: ""});
  const {user, access_token, keyPass, setKeyPass} = useAuthStore();
  const [isSub, setIsSub] = useState(false);
  const [client, setClient] = useState<MqttClient | null>(null);
  const [connectStatus, setConnectStatus] = useState("Disconnected");
  const [userMQTT, setUser] = useState("");

  const buildMQTTOptions = (username: any, pass: any): IClientOptions => ({
    keepalive: 10,
    protocolId: "MQTT",
    protocolVersion: 4,
    clean: true,
    reconnectPeriod: 2000,
    connectTimeout: 5 * 1000,
    rejectUnauthorized: true,
    username: `reportsw-vhost:${username}`,
    password: pass
  });

  const mqttConnect = (host: string, options: IClientOptions) => {
    setConnectStatus("Connecting");
    setClient(mqtt.connect(host, options));
  };

  const saveLogin = (val: ILogin) => {
    setUser(val.username);
    setKeyPass(btoa(val.password));
    if (!isSub) {
      const url = process.env.REACT_APP_MQTT_HOST;
      if (url) {
        mqttConnect(url, buildMQTTOptions(val.username, val.password));
      }
    }
  };

  const reconnectUser = () => {
    if (access_token && user && keyPass) {
      setUser(user.username + "");
      const url = process.env.REACT_APP_MQTT_HOST;
      if (url) {
        mqttConnect(url, buildMQTTOptions(user.username, atob(keyPass)));
      }
    }
  };

  useEffect(() => {
    reconnectUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (client && user) {
      const subscribeAll = () => {
        user?.reportQueues?.forEach((queue) => {
          mqttSub({ topic: `${queue}/#`, qos: 1 });
        });
      };

      const handleConnect = () => {
        console.log("Connecting conn");
        setConnectStatus("Connected");
        subscribeAll();
      };

      const handleReconnect = () => {
        console.log("Reconnecting conn");
        setConnectStatus("Reconnecting");
        subscribeAll();
      };

      const handleClose = () => {
        console.log("Closed conn");
        setConnectStatus("Disconnected");
      };

      const handlePing = () => {
        console.log("Keepalive ping sent to the broker");
      };
  

      client.on("connect", handleConnect);
      client.on("reconnect", handleReconnect);
      client.on("close", handleClose);
      client.on("pingreq", handlePing);
      client.on("error", (err) => {
        console.error("MQTT Error:", err);
      });
      client.on("message", (topic: string, message: Buffer) => {
        setPayload({ topic, message: message.toString() });
      });

      return () => {
        client.off("connect", handleConnect);
        client.off("reconnect", handleReconnect);
        client.off("close", handleClose);
        client.off("pingreq", handlePing);
        client.removeAllListeners();
      };
    }
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, user]);

  const mqttSub = ({topic, qos = 1}: { topic: string | string[]; qos: QoS }) => {
    if (client) {
      client.subscribe(topic, { qos }, (error) => {
        if (error) {
          console.error("Subscribe error:", error);
          return;
        }
        setIsSub(true);
      });
    }
  };

  const mqttUnSub = async () => {
    if (client && user?.reportQueues) {
      user.reportQueues.forEach((queue) => {
        client.unsubscribe(`${queue}/#`, (error: any) => {
          if (error) {
            console.error("Unsubscribe error:", error);
            return;
          }
          setIsSub(false);
        });
      });
    }
  };

  const mqttPublish = ({topic, qos = 1, payload}: {
    topic: string;
    qos: QoS;
    payload: string | Buffer;
  }) => {
    if (client) {
      client.publish(topic, payload, {qos}, (error) => {
        if (error) {
          console.error("Publish error:", error);
        }
      });
    }
  };

  const mqttDisconnect = async () => {
    if (client) {
      if (isSub) {
        await mqttUnSub();
      }
      client.end(false, {}, () => {
        setConnectStatus("Disconnected");
      });
    }
  };

  return {
    mqttConnect,
    mqttSub,
    mqttUnSub,
    mqttPublish,
    mqttDisconnect,
    payload,
    setPayload,
    isSub,
    saveLogin,
    userMQTT,
    connectStatus
  };
};

const MQTTContext: FC<TProps> = ({children}: TProps): JSX.Element => {
  const appState = useAppState();
  return <Context.Provider value={appState}>{children}</Context.Provider>;
};

export {MQTTContext, useMQTT};
